import React, { useState } from 'react';
import { formatDate } from '../services/dateUtils';
import { investigationService } from '../services/api';
import '../styles/ExportPanel.css';

const ExportPanel = ({ investigation, transactions, notes, tags, graphData }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [message, setMessage]         = useState('');

  const flash = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  const downloadBlob = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const stamp = () => new Date().toISOString().split('T')[0];

  // ── CSV ────────────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (!transactions || transactions.length === 0) {
      flash('✗ No transaction data to export');
      return;
    }
    setIsExporting(true);
    const headers = ['TX Hash', 'From Address', 'To Address', 'Value', 'Gas Price', 'Block', 'Timestamp', 'Token', 'Status'];
    const rows    = transactions.map((tx) => [
      tx.tx_hash      || '',
      tx.from_address || '',
      tx.to_address   || '',
      tx.value        ?? '',
      tx.gas_price    ?? '',
      tx.block_number ?? '',
      tx.timestamp    || '',
      tx.token_symbol || '',
      tx.status       || '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    downloadBlob(csv, `transactions-${investigation.id}-${stamp()}.csv`, 'text/csv');
    setIsExporting(false);
    flash('✓ Transactions exported as CSV');
  };

  // ── JSON ───────────────────────────────────────────────────────────────────
  const handleExportJSON = () => {
    if (!investigation) { flash('✗ No investigation data to export'); return; }
    setIsExporting(true);
    const payload = {
      investigation,
      transactions: transactions || [],
      notes:        notes        || [],
      tags:         tags         || [],
      graph:        graphData    || {},
      monitors:     graphData?.monitors || [],
      exported_at:  new Date().toISOString(),
    };
    downloadBlob(JSON.stringify(payload, null, 2), `investigation-${investigation.id}-${stamp()}.json`, 'application/json');
    setIsExporting(false);
    flash('✓ Investigation exported as JSON');
  };

  // ── Graph JSON ─────────────────────────────────────────────────────────────
  const handleExportGraph = () => {
    if (!graphData || !graphData.nodes?.length) { flash('✗ No graph data to export'); return; }
    const payload = {
      nodes:    graphData.nodes,
      edges:    graphData.edges,
      metadata: { investigation_id: investigation.id, title: investigation.title, exported_at: new Date().toISOString() },
    };
    downloadBlob(JSON.stringify(payload, null, 2), `graph-${investigation.id}-${stamp()}.json`, 'application/json');
    flash('✓ Graph exported as JSON');
  };

  // ── Text Report ────────────────────────────────────────────────────────────
  const handleExportText = () => {
    if (!investigation) { flash('✗ No investigation data to export'); return; }
    setIsExporting(true);
    const lines = [
      '═══════════════════════════════════════════════════════',
      '  NOCTIS LEDGER INVESTIGATION REPORT',
      '═══════════════════════════════════════════════════════',
      '',
      `Title:       ${investigation.title}`,
      `Blockchain:  ${investigation.blockchain?.toUpperCase()}`,
      `Address:     ${investigation.start_address}`,
      `Status:      ${investigation.status}`,
      `Created:     ${new Date(investigation.created_at).toLocaleString()}`,
      `Exported:    ${new Date().toLocaleString()}`,
      '',
      investigation.description ? `Description:\n${investigation.description}\n` : '',
      '───────────────────────────────────────────────────────',
      `GRAPH SUMMARY`,
      '───────────────────────────────────────────────────────',
      `Addresses:    ${graphData?.nodes?.length     || 0}`,
      `Transactions: ${graphData?.edges?.length     || 0}`,
      `High Risk:    ${graphData?.stats?.high_risk_count || 0}`,
      '',
      '───────────────────────────────────────────────────────',
      `TRANSACTIONS (${(transactions || []).length})`,
      '───────────────────────────────────────────────────────',
      ...(transactions || []).slice(0, 100).map((tx, i) =>
        `${i + 1}. ${tx.tx_hash}\n   From: ${tx.from_address}\n   To:   ${tx.to_address}\n   Value: ${tx.value} ${tx.token_symbol || ''}\n   Date: ${formatDate(tx.timestamp)}\n`
      ),
      '───────────────────────────────────────────────────────',
      `NOTES (${(notes || []).length})`,
      '───────────────────────────────────────────────────────',
      ...(notes || []).map((n, i) =>
        `${i + 1}. [${n.author}] ${new Date(n.created_at).toLocaleDateString()}\n   ${n.content}\n`
      ),
      '───────────────────────────────────────────────────────',
      `TAGS (${(tags || []).length})`,
      '───────────────────────────────────────────────────────',
      ...(tags || []).map((t) => `${t.address} → ${t.tag}${t.notes ? ` (${t.notes})` : ''}`),
      '',
      '───────────────────────────────────────────────────────',
      `MONITORED ADDRESSES (${(graphData?.monitors || []).length})`,
      '───────────────────────────────────────────────────────',
      ...(graphData?.monitors || []).length === 0
        ? ['No addresses were under active live monitoring for this investigation.']
        : (graphData.monitors).map((m, i) => {
            const origin = m.cascade
              ? `Cascade — depth ${m.depth ?? 0}${m.parent_address ? `, from ${m.parent_address}` : ''}`
              : 'Root (manually added)';
            return `${i + 1}. ${m.address}${m.label ? ` (${m.label})` : ''}\n   Origin: ${origin}\n   Since: ${m.added_at ? new Date(m.added_at).toLocaleString() : '—'}\n`;
          }),
      '',
      '═══════════════════════════════════════════════════════',
      '  END OF REPORT',
      '═══════════════════════════════════════════════════════',
    ];
    downloadBlob(lines.join('\n'), `report-${investigation.id}-${stamp()}.txt`, 'text/plain');
    setIsExporting(false);
    flash('✓ Report exported as text');
  };

  // ── PDF Report (court-ready) ─────────────────────────────────────────────
  const handleExportPdfReport = async () => {
    if (!investigation) { flash('✗ No investigation data to export'); return; }
    setIsExporting(true);
    flash('⏳ Generating PDF report…');
    try {
      // Grab a snapshot of the live graph canvas if the Graph tab has rendered one
      // (exposed by Graph.js). Falls back to no image if the graph hasn't mounted
      // in this session - the backend still produces the rest of the report.
      const graphImage = typeof window.__mainGraphExportPng === 'function'
        ? window.__mainGraphExportPng()
        : null;

      const res = await investigationService.generateReport(investigation.id, graphImage);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `noctis-report-${investigation.id}-${stamp()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      flash('✓ PDF report generated');
    } catch (e) {
      console.error('PDF report generation failed:', e);
      flash('✗ PDF report generation failed');
    } finally {
      setIsExporting(false);
    }
  };

  // ── GEXF (Gephi) ──────────────────────────────────────────────────────────
  const handleExportGEXF = () => {
    if (!graphData || !graphData.nodes?.length) { flash('✗ No graph data to export'); return; }
    const nodes = (graphData.nodes || []).map((n) =>
      `    <node id="${n.id}" label="${(n.label || n.id).replace(/&/g, '&amp;').replace(/"/g, '&quot;')}"/>`
    ).join('\n');
    const edges = (graphData.edges || []).map((e, i) =>
      `    <edge id="${i}" source="${e.from}" target="${e.to}" weight="${e.value || 1}"/>`
    ).join('\n');
    const gexf = `<?xml version="1.0" encoding="UTF-8"?>
<gexf xmlns="http://gexf.net/1.3" version="1.3">
  <meta lastmodifieddate="${stamp()}">
    <creator>Noctis Ledger</creator>
    <description>${investigation.title}</description>
  </meta>
  <graph defaultedgetype="directed">
    <nodes>
${nodes}
    </nodes>
    <edges>
${edges}
    </edges>
  </graph>
</gexf>`;
    downloadBlob(gexf, `graph-${investigation.id}-${stamp()}.gexf`, 'application/xml');
    flash('✓ Graph exported as GEXF (Gephi)');
  };

  return (
    <div className="export-panel">
      <h3>Export Investigation</h3>

      <div className="export-options">
        <div className="export-section">
          <h4>Full Investigation</h4>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={handleExportJSON} disabled={isExporting || !investigation} className="export-button">
              📄 JSON
            </button>
            <button onClick={handleExportText} disabled={isExporting || !investigation} className="export-button">
              📝 Text Report
            </button>
            <button onClick={handleExportPdfReport} disabled={isExporting || !investigation} className="export-button highlight">
              🧭 PDF Report (Court-Ready)
            </button>
          </div>
        </div>

        <div className="export-section">
          <h4>Transaction Data</h4>
          <button onClick={handleExportCSV} disabled={isExporting || !transactions?.length} className="export-button secondary">
            💾 CSV (Excel)
          </button>
        </div>

        <div className="export-section">
          <h4>Graph Visualization</h4>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={handleExportGraph} disabled={!graphData?.nodes?.length} className="export-button secondary">
              📊 Graph JSON
            </button>
            <button onClick={handleExportGEXF} disabled={!graphData?.nodes?.length} className="export-button secondary">
              🕸️ GEXF (Gephi)
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div className={`export-message ${message.startsWith('✓') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="export-info">
        <h4>Format Guide</h4>
        <ul>
          <li><strong>JSON:</strong> Complete investigation data — investigations, transactions, notes, tags, monitored addresses, graph</li>
          <li><strong>Text Report:</strong> Human-readable summary for printing or sharing</li>
          <li><strong>PDF Report:</strong> Court-ready export with fund-flow narrative, attributed entities, risk scores, transaction timeline, and a graph snapshot</li>
          <li><strong>CSV:</strong> Transactions in spreadsheet format for Excel / Google Sheets</li>
          <li><strong>Graph JSON:</strong> Node/edge data for custom visualisation tools</li>
          <li><strong>GEXF:</strong> Graph format for Gephi network analysis software</li>
        </ul>
      </div>

      <div className="export-stats">
        <h4>Data Summary</h4>
        <div className="stats-grid">
          <div className="stat"><span className="stat-label">Transactions:</span><span className="stat-value">{transactions?.length || 0}</span></div>
          <div className="stat"><span className="stat-label">Notes:</span><span className="stat-value">{notes?.length || 0}</span></div>
          <div className="stat"><span className="stat-label">Tags:</span><span className="stat-value">{tags?.length || 0}</span></div>
          <div className="stat"><span className="stat-label">Graph Nodes:</span><span className="stat-value">{graphData?.nodes?.length || 0}</span></div>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;
